import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, Sparkles, Printer, Users, Grid3x3 } from "lucide-react";

/**
 * /demo-video — Public, unauthed, self-contained 90-second cinematic demo reel
 * designed for vertical (9:16) screen recording (TikTok / Reels / Shorts).
 *
 * How to use:
 *   1. Open on a phone (portrait) OR resize a browser to a 9:16 window.
 *   2. Hit "Start recording" in iOS Screen Recorder / OBS / Loom.
 *   3. Click "▶ Play demo" — you get a 3-second countdown, then 90 s of demo,
 *      then a freeze on the CTA frame.
 *
 * The reel narrates itself with big captions. No audio (dub voice in editor).
 *
 * Scenes (90 s total):
 *   0. Countdown         (3 s)   — 3-2-1 pre-roll so you can hit Record
 *   1. Hook              (8 s)   — dark, brand, promise
 *   2. Build cards       (14 s)  — therapist adds stimuli one by one
 *   3. Build array       (12 s)  — cards snap into a 2x2 grid
 *   4. Learner runs it   (26 s)  — two Match rounds with a finger cursor + celebration
 *   5. Features montage  (17 s)  — sorting, printables, team libraries
 *   6. CTA               (10 s)  — logo + "Try free at discrimina.app"
 */

const TOTAL_MS = 90_000;
const SCENES = [
  { id: "countdown",  ms: 3_000  },
  { id: "hook",       ms: 8_000  },
  { id: "cards",      ms: 14_000 },
  { id: "array",      ms: 12_000 },
  { id: "learner",    ms: 26_000 },
  { id: "montage",    ms: 17_000 },
  { id: "cta",        ms: 10_000 },
];
// cumulative end-times for each scene
const CUM = SCENES.reduce((acc, s, i) => {
  acc.push((acc[i - 1] || 0) + s.ms);
  return acc;
}, []);

function sceneAt(t) {
  for (let i = 0; i < CUM.length; i++) if (t < CUM[i]) return { i, elapsed: t - (CUM[i - 1] || 0), scene: SCENES[i] };
  return { i: SCENES.length - 1, elapsed: SCENES[SCENES.length - 1].ms, scene: SCENES[SCENES.length - 1] };
}

export default function DemoVideoPage() {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);

  useEffect(() => {
    if (!playing) return;
    const start = performance.now() - t;
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      if (elapsed >= TOTAL_MS) {
        setT(TOTAL_MS);
        setPlaying(false);
        return;
      }
      setT(elapsed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const { i, elapsed, scene } = sceneAt(t);
  const finished = t >= TOTAL_MS;
  const progress = Math.min(1, t / TOTAL_MS);

  const start = () => { setT(0); setPlaying(true); };
  const reset = () => { setT(0); setPlaying(false); };

  return (
    <div className="min-h-screen w-full bg-slate-950 flex flex-col items-center justify-center p-4 text-white">
      {/* Instructions row (hidden while playing so screen recording is clean) */}
      {!playing && !finished && (
        <div className="mb-4 text-center max-w-md" data-testid="demo-video-instructions">
          <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300 font-bold">Discrimina · Demo Reel</div>
          <div className="font-display text-2xl font-extrabold mt-1">90-second vertical demo</div>
          <div className="text-xs text-slate-400 mt-2">
            Start your screen recorder first, then hit ▶ Play. 3-second countdown, then 90 seconds of demo.
            Best on a phone in portrait, or a 9:16 browser window.
          </div>
        </div>
      )}

      {/* 9:16 Stage */}
      <div
        className="relative bg-white rounded-[36px] overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
        style={{ width: "min(420px, calc(100vh * 9 / 16 - 32px))", aspectRatio: "9/16" }}
        data-testid="demo-video-stage"
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 z-40 bg-black/10">
          <div className="h-full bg-teal-500 transition-[width] duration-100 ease-linear" style={{ width: `${progress * 100}%` }} data-testid="demo-video-progress" />
        </div>

        <AnimatePresence mode="wait">
          {scene.id === "countdown" && <SceneCountdown key="c" elapsed={elapsed} />}
          {scene.id === "hook"      && <SceneHook      key="h" elapsed={elapsed} />}
          {scene.id === "cards"     && <SceneCards     key="k" elapsed={elapsed} />}
          {scene.id === "array"     && <SceneArray     key="a" elapsed={elapsed} />}
          {scene.id === "learner"   && <SceneLearner   key="l" elapsed={elapsed} />}
          {scene.id === "montage"   && <SceneMontage   key="m" elapsed={elapsed} />}
          {scene.id === "cta"       && <SceneCta       key="t" elapsed={elapsed} />}
        </AnimatePresence>

        {/* Play overlay (before start) */}
        {!playing && !finished && t === 0 && (
          <button
            onClick={start}
            data-testid="demo-video-play"
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/85 backdrop-blur-sm"
          >
            <div className="w-20 h-20 rounded-full bg-teal-500 flex items-center justify-center shadow-2xl">
              <Play className="w-8 h-8 text-white fill-white ml-1" />
            </div>
            <div className="mt-4 text-white/90 font-semibold text-sm">Play 90-second demo</div>
            <div className="text-white/50 text-xs mt-1">Starts with a 3-second countdown</div>
          </button>
        )}
      </div>

      {/* Post-play controls */}
      {(playing || finished) && (
        <button
          onClick={reset}
          data-testid="demo-video-restart"
          className="mt-4 inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-white/10 text-white text-xs font-semibold hover:bg-white/20"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Restart
        </button>
      )}
    </div>
  );
}

/* ─────────── Scene 0 · Countdown ─────────── */
function SceneCountdown({ elapsed }) {
  const n = Math.max(1, 3 - Math.floor(elapsed / 1000));
  return (
    <motion.div key={`cd-${n}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-slate-950 flex items-center justify-center">
      <motion.div key={n} initial={{ scale: 2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
        className="font-display text-[140px] font-extrabold text-teal-400">
        {n}
      </motion.div>
    </motion.div>
  );
}

/* ─────────── Scene 1 · Hook ─────────── */
function SceneHook() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-slate-950 text-white flex flex-col items-center justify-center px-8 text-center">
      <motion.img
        src="/icon-192.png" alt="Discrimina" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6, type: "spring" }} className="w-20 h-20 rounded-2xl mb-6"
      />
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
        className="text-[11px] uppercase tracking-[0.28em] text-teal-300 font-bold mb-3">Discrimina</motion.div>
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.0 }}
        className="font-display text-[42px] leading-[1.05] font-extrabold">
        Design + run<br/><span className="text-teal-400">DTT drills</span><br/>in one place.
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.5 }}
        className="mt-6 text-sm text-slate-400">No more Canva. No more PowerPoint.</motion.div>
    </motion.div>
  );
}

/* ─────────── Scene 2 · Build cards ─────────── */
const CARDS = [
  { label: "Apple", tag: "TACT", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { label: "Ball",  tag: "TACT", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { label: "Bat",   tag: "TACT", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { label: "Boat",  tag: "TACT", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];
function SceneCards({ elapsed }) {
  const visible = Math.min(4, Math.floor(elapsed / 2200) + 1);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-slate-50 flex flex-col p-6 pt-14">
      <Caption>Add stimuli</Caption>
      <div className="mt-6 space-y-3">
        {CARDS.map((c, idx) => (
          <AnimatePresence key={c.label}>
            {visible > idx && (
              <motion.div
                initial={{ x: 300, opacity: 0, rotate: 8 }} animate={{ x: 0, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 140, damping: 18 }}
                className="bg-white rounded-2xl p-4 shadow-md border border-slate-200 flex items-center gap-4"
                data-testid={`demo-card-${c.label.toLowerCase()}`}>
                <StimulusIcon label={c.label} />
                <div className="flex-1">
                  <div className="font-display text-xl font-extrabold text-slate-900">{c.label}</div>
                  <div className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border ${c.color}`}>{c.tag}</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
      {visible === 4 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-6 text-center text-slate-500 text-xs font-semibold">
          Colour-coded operants. Zero setup.
        </motion.div>
      )}
    </motion.div>
  );
}

/* ─────────── Scene 3 · Build array ─────────── */
function SceneArray({ elapsed }) {
  const stage = elapsed < 4000 ? "spread" : elapsed < 8000 ? "settle" : "labelled";
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-slate-50 flex flex-col p-6 pt-14">
      <Caption>Build the array</Caption>
      <div className="flex-1 flex items-center justify-center relative">
        <div className="grid grid-cols-2 gap-3 w-full max-w-[300px]" data-testid="demo-array-grid">
          {CARDS.map((c, idx) => (
            <motion.div
              key={c.label}
              initial={{ scale: 0.4, opacity: 0, rotate: (idx % 2 === 0 ? -15 : 15) }}
              animate={{
                scale: 1,
                opacity: 1,
                rotate: stage === "spread" ? (idx % 2 === 0 ? -6 : 6) : 0,
              }}
              transition={{ delay: idx * 0.25, type: "spring", stiffness: 140, damping: 15 }}
              className="aspect-square bg-white rounded-2xl border-2 border-slate-200 shadow-sm flex flex-col items-center justify-center p-3">
              <StimulusIcon label={c.label} size="lg" />
              <div className="mt-2 font-display font-extrabold text-slate-900 text-sm">{c.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
      {stage === "labelled" && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-center text-slate-500 text-xs font-semibold">
          2×2 · Non-identical matching · Randomised
        </motion.div>
      )}
    </motion.div>
  );
}

/* ─────────── Scene 4 · Learner runs it ─────────── */
const ROUNDS = [
  { target: "Ball",  correct: "Ball"  },
  { target: "Apple", correct: "Apple" },
];
function SceneLearner({ elapsed }) {
  // 26s total, 2 rounds ≈ 13s each
  const roundIdx = Math.min(ROUNDS.length - 1, Math.floor(elapsed / 13000));
  const localMs = elapsed - roundIdx * 13000;
  const round = ROUNDS[roundIdx];
  // Round micro-timeline (ms):
  // 0-1200:   nothing (small pause after scene switch)
  // 1200-3200:SD banner slide in
  // 3200-7500:finger cursor slides to target
  // 7500-9800:card glows green + celebration
  // 9800-13000: hold + fade
  const showSd = localMs > 1200;
  const showFinger = localMs > 3200;
  const showCelebration = localMs > 7500;

  // Compute finger cursor position based on target
  const targetIdx = CARDS.findIndex((c) => c.label === round.correct);
  const col = targetIdx % 2, row = Math.floor(targetIdx / 2);
  const fingerX = col === 0 ? 25 : 75;   // % of grid
  const fingerY = row === 0 ? 25 : 75;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-slate-950 flex flex-col p-4 pt-14 text-white">
      <Caption tone="dark">At the table</Caption>

      {/* iPad-frame styling */}
      <div className="mt-4 flex-1 flex flex-col items-center justify-center">
        <div className="w-full bg-white/95 rounded-2xl p-4 border border-white/10 shadow-2xl">
          <AnimatePresence>
            {showSd && (
              <motion.div key={`sd-${roundIdx}`}
                initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                transition={{ type: "spring", stiffness: 140, damping: 16 }}
                className="bg-teal-600 text-white rounded-xl py-3 text-center font-display font-extrabold text-xl shadow-lg mb-4"
                data-testid={`demo-sd-${roundIdx}`}>
                Touch the {round.target.toLowerCase()}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative grid grid-cols-2 gap-2.5" style={{ aspectRatio: "1" }}>
            {CARDS.map((c) => {
              const isCorrect = c.label === round.correct;
              return (
                <motion.div
                  key={`${roundIdx}-${c.label}`}
                  animate={{
                    scale: showCelebration && isCorrect ? 1.08 : 1,
                    borderColor: showCelebration && isCorrect ? "#059669" : "#e2e8f0",
                    backgroundColor: showCelebration && isCorrect ? "#d1fae5" : "#ffffff",
                  }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                  className="aspect-square rounded-xl border-2 bg-white flex flex-col items-center justify-center p-2 relative">
                  <StimulusIcon label={c.label} size="md" />
                  <div className="mt-1 font-display font-extrabold text-slate-900 text-xs">{c.label}</div>
                  {showCelebration && isCorrect && (
                    <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 12 }}
                      className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                      <Sparkles className="w-4 h-4 text-white" />
                    </motion.div>
                  )}
                </motion.div>
              );
            })}

            {/* Finger cursor */}
            {showFinger && !showCelebration && (
              <motion.div
                key={`finger-${roundIdx}`}
                initial={{ left: "50%", top: "50%", scale: 0 }}
                animate={{ left: `${fingerX}%`, top: `${fingerY}%`, scale: 1 }}
                transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute w-14 h-14 rounded-full bg-black/50 border-4 border-white shadow-2xl pointer-events-none"
                style={{ transform: "translate(-50%, -50%)" }}
              />
            )}
          </div>
        </div>
        {showCelebration && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-center text-emerald-300 font-display text-lg font-extrabold">
            ✓ Matched · Round {roundIdx + 1}
          </motion.div>
        )}
      </div>
      <div className="text-center text-slate-500 text-xs font-semibold pb-2">Tap-to-select · non-identical</div>
    </motion.div>
  );
}

/* ─────────── Scene 5 · Feature montage ─────────── */
function SceneMontage({ elapsed }) {
  const beat = Math.min(2, Math.floor(elapsed / 5500));
  const beats = [
    { icon: <Grid3x3 className="w-14 h-14 text-teal-600" />, label: "Sorting", sub: "Drag & drop into any category" },
    { icon: <Printer className="w-14 h-14 text-teal-600" />, label: "Printable materials", sub: "One-tap PDF supervision sheets" },
    { icon: <Users className="w-14 h-14 text-teal-600" />, label: "Team libraries", sub: "Share stimuli across your clinic" },
  ];
  const b = beats[beat];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-white flex flex-col p-6 pt-14">
      <Caption>More in the box</Caption>
      <div className="flex-1 flex flex-col items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={beat}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 140, damping: 16 }}
            className="text-center">
            <div className="w-28 h-28 rounded-3xl bg-teal-50 flex items-center justify-center mx-auto shadow-inner border border-teal-100">
              {b.icon}
            </div>
            <div className="mt-6 font-display text-3xl font-extrabold text-slate-900">{b.label}</div>
            <div className="mt-2 text-slate-500 text-sm">{b.sub}</div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex justify-center gap-1.5 pb-2">
        {beats.map((_, i) => (
          <div key={i} className={`h-1 w-6 rounded-full transition-colors ${i <= beat ? "bg-teal-500" : "bg-slate-200"}`} />
        ))}
      </div>
    </motion.div>
  );
}

/* ─────────── Scene 6 · CTA ─────────── */
function SceneCta() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 flex flex-col items-center justify-center p-8 text-white text-center">
      <motion.img
        src="/icon-192.png" alt="Discrimina" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 140, damping: 12 }}
        className="w-24 h-24 rounded-3xl shadow-2xl ring-4 ring-white/20"
      />
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
        className="mt-6 font-display text-[44px] leading-[1.05] font-extrabold">
        Discrimina
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
        className="mt-3 text-white/85 text-base font-semibold">Design + run DTT drills in minutes.</motion.div>
      <motion.a
        href="https://discrimina.app" target="_blank" rel="noreferrer"
        initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.4, type: "spring" }}
        className="mt-10 px-8 h-14 rounded-full bg-white text-teal-700 font-display font-extrabold text-lg shadow-xl inline-flex items-center gap-2 hover:bg-teal-50"
        data-testid="demo-cta-link">
        Try free →
      </motion.a>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }}
        className="mt-6 font-mono text-white/90 text-sm tracking-widest">discrimina.app</motion.div>
    </motion.div>
  );
}

/* ─────────── Shared ─────────── */
function Caption({ children, tone = "light" }) {
  const bg = tone === "dark" ? "bg-white/10 text-teal-300 border-white/20" : "bg-teal-50 text-teal-700 border-teal-200";
  return (
    <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      className={`absolute top-6 left-1/2 -translate-x-1/2 px-4 h-8 rounded-full border ${bg} text-[11px] uppercase tracking-[0.2em] font-bold flex items-center`}>
      {children}
    </motion.div>
  );
}

/**
 * Emoji-free stimulus icon: a coloured shape that reads at a glance so the
 * viewer knows we're showing real DTT stimuli, not text placeholders.
 */
function StimulusIcon({ label, size = "md" }) {
  const dim = size === "lg" ? "w-16 h-16 text-2xl" : size === "md" ? "w-12 h-12 text-xl" : "w-10 h-10 text-lg";
  const svg = useMemo(() => {
    switch (label) {
      case "Apple": return (
        <svg viewBox="0 0 40 40" className="w-full h-full">
          <path d="M20 12 C20 6, 24 4, 26 8" stroke="#166534" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          <ellipse cx="20" cy="26" rx="12" ry="12" fill="#ef4444"/>
          <ellipse cx="16" cy="22" rx="3" ry="4" fill="#fca5a5" opacity="0.6"/>
        </svg>
      );
      case "Ball": return (
        <svg viewBox="0 0 40 40" className="w-full h-full">
          <circle cx="20" cy="20" r="14" fill="#f59e0b"/>
          <line x1="20" y1="6" x2="20" y2="34" stroke="#78350f" strokeWidth="1.5"/>
          <line x1="6" y1="20" x2="34" y2="20" stroke="#78350f" strokeWidth="1.5"/>
        </svg>
      );
      case "Bat": return (
        <svg viewBox="0 0 40 40" className="w-full h-full">
          <rect x="18" y="6" width="4" height="24" rx="2" fill="#78350f" transform="rotate(-25 20 20)"/>
          <ellipse cx="12" cy="30" rx="7" ry="4" fill="#a16207" transform="rotate(-25 20 20)"/>
        </svg>
      );
      case "Boat": return (
        <svg viewBox="0 0 40 40" className="w-full h-full">
          <path d="M20 8 L20 26 L10 26 Z" fill="#0284c7"/>
          <path d="M22 10 L34 26 L22 26 Z" fill="#38bdf8"/>
          <path d="M6 28 Q10 34, 20 34 T34 28 L32 32 Q26 34, 20 34 T8 32 Z" fill="#0369a1"/>
        </svg>
      );
      default: return <div className={`${dim} rounded-full bg-slate-200`} />;
    }
  }, [label]);
  return <div className={`${dim} shrink-0 flex items-center justify-center`}>{svg}</div>;
}
